import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent } from '@/lib/track';
import { getPageType, getVixiProductFromPath, normalizeSolution } from '@/lib/analytics-taxonomy';
import { useScrollDepth } from '@/hooks/useAnalytics';

/** Resolve a human-readable location label for the clicked element. */
function resolveLocation(el: Element): string {
  const explicit = el.closest('[data-analytics-location]') as HTMLElement | null;
  if (explicit?.dataset.analyticsLocation) return explicit.dataset.analyticsLocation;
  if (el.closest('header')) return 'header';
  if (el.closest('footer')) return 'footer';
  if (el.closest('[role="dialog"]')) return 'modal';
  const section = el.closest('section[id]') as HTMLElement | null;
  if (section?.id) return section.id;
  if (el.closest('nav')) return 'navigation';
  return 'page_body';
}

function textOf(el: Element): string {
  return (el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 100);
}

const DEMO_RE = /\b(demo|see it in action|book a (call|demo))\b/i;
const CONTACT_RE = /\b(contact|let'?s talk|get started|get in touch|talk to (us|sales)|request a quote|work with us)\b/i;
const BUTTON_LIKE_RE = /\b(rounded-full|rounded-md|rounded-xl|rounded-lg)\b/;
const SKIP_TEXT_RE = /^(close|close ✕|✕|menu|×)$/i;

function isExternalHost(url: URL): boolean {
  const self = window.location.hostname.replace(/^www\./, '');
  const host = url.hostname.replace(/^www\./, '');
  return host !== self && !host.endsWith(`.${self}`);
}

/**
 * Site-wide analytics instrumentation:
 *  - scroll_depth (50 / 90) per page view
 *  - solution_view / vixi_product_view on meaningful route entry
 *  - delegated click tracking: contact / demo / email / phone / outbound / linkedin / cta
 *
 * One delegated listener handles every CTA across the site, so React re-renders
 * cannot attach duplicate handlers and no presentational component changes.
 */
const AnalyticsInstrumentation = () => {
  const location = useLocation();

  useScrollDepth([50, 90]);

  // Route-level content views
  useEffect(() => {
    const path = location.pathname;
    const pageType = getPageType(path);

    if (pageType === 'vixi_product') {
      const product = getVixiProductFromPath(path);
      if (product) {
        trackEvent('vixi_product_view', { product_family: 'vixi', product });
      }
      return;
    }

    if (pageType === 'vixi') {
      trackEvent('solution_view', { solution: 'vixi' });
      return;
    }

    if (pageType === 'solutions') {
      // Deep-linked solution (e.g. /solutions?solution=mixed-reality or #mixed-reality)
      const params = new URLSearchParams(location.search);
      const deepLink = params.get('solution') || location.hash.replace('#', '');
      const solution = normalizeSolution(deepLink);
      if (solution) trackEvent('solution_view', { solution });
    }
  }, [location.pathname, location.search, location.hash]);

  // Delegated click tracking
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (!target) return;
      const el = target.closest('a, button') as HTMLAnchorElement | HTMLButtonElement | null;
      if (!el || el.hasAttribute('data-analytics-skip')) return;

      const label = textOf(el) || el.getAttribute('aria-label') || '';
      if (SKIP_TEXT_RE.test(label)) return;

      const location_ = resolveLocation(el);
      const rawHref = el instanceof HTMLAnchorElement ? el.getAttribute('href') || '' : '';

      // mailto / tel — never send the address or number itself
      if (rawHref.startsWith('mailto:')) {
        trackEvent('email_click', { link_location: location_ });
        return;
      }
      if (rawHref.startsWith('tel:')) {
        trackEvent('phone_click', { link_location: location_ });
        return;
      }

      let destination = rawHref;
      let external = false;
      if (rawHref && !rawHref.startsWith('#')) {
        try {
          const url = new URL(rawHref, window.location.origin);
          external = isExternalHost(url);
          destination = external ? url.href : url.pathname + url.search;
        } catch {
          /* keep raw href */
        }
      }

      if (external) {
        const host = new URL(destination).hostname.replace(/^www\./, '');
        if (host.includes('linkedin.com')) {
          trackEvent('linkedin_click', { link_location: location_, destination });
        } else {
          trackEvent('outbound_click', {
            destination_domain: host,
            link_text: label,
            link_location: location_,
          });
        }
        return;
      }

      const path = window.location.pathname;
      const product = getVixiProductFromPath(path);
      const solutionFromContext = normalizeSolution(
        (el.closest('[data-analytics-solution]') as HTMLElement | null)?.dataset.analyticsSolution
      );

      const goesToContact = destination.startsWith('/contact');
      const isDemo = DEMO_RE.test(label);

      if (isDemo) {
        trackEvent('request_demo_click', {
          cta_text: label,
          cta_location: location_,
          destination: destination || undefined,
          solution: solutionFromContext,
          product,
        });
        return;
      }

      if (goesToContact || (CONTACT_RE.test(label) && !rawHref)) {
        trackEvent('contact_click', {
          cta_text: label,
          cta_location: location_,
          destination: destination || '/contact',
        });
        return;
      }

      // Generic meaningful CTA: styled buttons only — ordinary nav/footer links are excluded
      const isNavLink = Boolean(el.closest('header') || el.closest('footer') || el.closest('nav'));
      const looksLikeButton =
        el.tagName === 'BUTTON' || BUTTON_LIKE_RE.test(el.className?.toString() || '');
      if (!isNavLink && looksLikeButton && label) {
        trackEvent('cta_click', {
          cta_text: label,
          cta_location: location_,
          destination: destination || undefined,
        });
      }
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  return null;
};

export default AnalyticsInstrumentation;
